import { ref, computed } from 'vue'
import aa, { type InsightsEventObjectData } from 'search-insights'

/**
 * Configuration interface for Algolia Insights initialization.
 *
 * @interface AlgoliaInsightsConfig
 * @property {string} appId - Algolia application ID
 * @property {string} apiKey - Algolia API key
 * @property {string} [userToken] - Optional user token for personalization
 */
export interface AlgoliaInsightsConfig {
  appId: string
  apiKey: string
  userToken?: string
}

/**
 * Composable for managing Algolia Insights tracking and events.
 * Provides methods to track various user interactions with search results.
 *
 * @returns {Object} An object containing Algolia Insights tracking methods and state
 * @property {Ref<boolean>} insightsInitialized - Indicates if Algolia Insights is initialized
 * @property {Function} setSearchQueryID - Sets the current search query ID
 * @property {Ref<string>} searchQueryID - Current search query ID
 * @property {Function} trackClickedItem - Tracks clicked items in search results
 * @property {Function} viewProduct - Tracks product detail page views
 * @property {Function} addedToCartfromPDP - Tracks add-to-cart events from product detail page
 * @property {Function} addedToCartfromPLP - Tracks add-to-cart events from product listing page
 * @property {Function} purchasedProduct - Tracks product purchase events
 */
export function useAlgoliaInsights() {
  const insightsInitialized = ref(false)

  const algoliaUserToken = computed(() => useCookie('_ALGOLIA').value)
  const runtimeConfig = useRuntimeConfig()
  const { isLogged } = useLogin()

  // Watch login state and set authenticated user token
  watch(
    isLogged,
    async value => {
      if (value) {
        const { customer } = await useCustomer()
        const customerId = customer?.value.id.replace('auth0|', '')
        aa('setAuthenticatedUserToken', customerId)
      } else {
        aa('setAuthenticatedUserToken', undefined)
      }
    },
    { immediate: true }
  )

  /**
   * State for storing the last search query ID.
   * @type {Ref<string>}
   */
  const searchQueryID = useState<string>(
    'useAlgoliaInsights__queryID',
    () => ''
  )

  // Initialize Algolia Insights client (client-side only)
  if (typeof window !== 'undefined') {
    // Prevent multiple initializations
    if (
      !(window as any).ALGOLIA_INSIGHTS_INITIALIZED_FOR_APP ||
      (window as any).ALGOLIA_INSIGHTS_INITIALIZED_FOR_APP !==
        runtimeConfig.public.algolia.applicationId
    ) {
      aa('init', {
        appId: runtimeConfig.public.algolia.applicationId,
        apiKey: runtimeConfig.public.algolia.apiKey,
        useCookie: true, // Consider privacy implications
        userToken: algoliaUserToken.value || undefined,
      })
      ;(window as any).ALGOLIA_INSIGHTS_INITIALIZED_FOR_APP =
        runtimeConfig.public.algolia.applicationId
    }
    insightsInitialized.value = true
  }

  /**
   * Sets the current search query ID.
   *
   * @param {string | null} value - The search query ID to set
   * @example
   * setSearchQueryID('query-id-123');
   */
  const setSearchQueryID = (value: string | null) => {
    searchQueryID.value = value || ''
  }

  /**
   * Tracks clicked items in search results.
   *
   * @param {string} index - Algolia index name
   * @param {string} eventName - Custom event name
   * @param {string[]} objectIDs - Array of clicked object IDs
   * @param {string} queryID - Search query ID
   * @param {number[]} positions - Positions of clicked items
   */
  const trackClickedItem = (
    index: string,
    eventName: string,
    objectIDs: string[],
    queryID: string,
    positions: number[]
  ) => {
    const qID = queryID || searchQueryID.value
    if (!qID) {
      return
    }
    aa('clickedObjectIDsAfterSearch', {
      userToken: algoliaUserToken.value || undefined,
      index,
      eventName,
      objectIDs,
      queryID: qID,
      positions: positions ?? [],
    })
  }

  /**
   * Tracks add-to-cart events from Product Detail Page (PDP).
   *
   * @param {string} index - Algolia index name
   * @param {string} eventName - Custom event name
   * @param {string[]} objectIDs - Array of product object IDs added to cart
   * @param {string} [queryID] - Optional search query ID
   * @example
   * addedToCartfromPDP('products_index', 'Product Added To Cart', ['123']);
   */
  const addedToCartfromPDP = (
    index: string,
    eventName: string,
    objectIDs: string[],
    queryID?: string
  ) => {
    if (!searchQueryID.value) {
      aa('addedToCartObjectIDs', {
        userToken: algoliaUserToken.value || undefined,
        index,
        eventName,
        objectIDs,
      })
    } else {
      const qID = queryID || searchQueryID.value
      if (!qID) {
        return
      }
      aa('addedToCartObjectIDsAfterSearch', {
        userToken: algoliaUserToken.value || undefined,
        index,
        queryID: qID,
        eventName,
        objectIDs,
      })
    }
  }

  /**
   * Tracks add-to-cart events from Product Listing Page (PLP).
   *
   * @param {string} index - Algolia index name
   * @param {string} eventName - Custom event name
   * @param {string} queryID - Search query ID
   * @param {string[]} objectIDs - Array of product object IDs added to cart
   * @example
   * addedToCartfromPLP('products_index', 'Product Added To Cart', 'query-id', ['123']);
   */
  const addedToCartfromPLP = (
    index: string,
    eventName: string,
    queryID: string,
    objectIDs: string[]
  ) => {
    const qID = queryID || searchQueryID.value
    if (!qID) {
      return
    }
    aa('addedToCartObjectIDsAfterSearch', {
      userToken: algoliaUserToken.value || undefined,
      index,
      queryID: qID,
      eventName,
      objectIDs,
    })
  }

  /**
   * Tracks product detail view events.
   *
   * @param {string} index - Algolia index name
   * @param {string} eventName - Custom event name
   * @param {string[]} objectIDs - Array of viewed product object IDs
   * @example
   * viewProduct('products_index', 'Product Viewed', ['123']);
   */
  const viewProduct = (
    index: string,
    eventName: string,
    objectIDs: string[]
  ) => {
    aa('viewedObjectIDs', {
      userToken: algoliaUserToken.value || undefined,
      index,
      eventName,
      objectIDs,
    })
  }

  /**
   * Tracks product purchase events.
   *
   * @param {string} index - Algolia index name
   * @param {string} eventName - Custom event name
   * @param {string[]} objectIDs - Array of purchased product object IDs
   * @param {InsightsEventObjectData[]} [objectData] - Optional detailed product data
   * @param {string} [currency] - Optional currency code
   * @example
   * purchasedProduct('products_index', 'Product Purchase', ['123'], [{}], 'USD');
   */
  const purchasedProduct = (
    index: string,
    eventName: string,
    objectIDs: string[],
    objectData?: InsightsEventObjectData[],
    currency?: string
  ) => {
    aa('purchasedObjectIDsAfterSearch', {
      userToken: algoliaUserToken.value || undefined,
      index,
      eventName,
      objectIDs,
      objectData,
      currency,
    })
  }

  return {
    insightsInitialized,
    setSearchQueryID,
    searchQueryID,
    trackClickedItem,
    viewProduct,
    addedToCartfromPDP,
    addedToCartfromPLP,
    purchasedProduct,
  }
}
